<template>
  <section class="node-pools-table-header">
    <div :class="['node-pools-table-header__row', { 'scheduling-tab': isSchedulingTab }]">
      <div v-for="header in labels" :key="header.name" class="node-pools-table-header__cell" :aid="header.aid">
        <div v-html="getHeaderLabel(header)" />
        <runai-tooltip
          width="287px"
          v-if="header.tooltipText"
          size="xs"
          tooltip-position="right"
          :tooltip-text="header.tooltipText"
        />
      </div>
    </div>
  </section>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { EQuotaEntity } from "@/models/resource.model";
import { EQuotaTab } from "@/models/org-unit.model";
import { RunaiTooltip } from "@/components/common/runai-tooltip";
import { EQuotaHeaderLabel, EQuotaHeaderName, type HeaderConfig } from "./node-pools-table-header.model";
import { stringUtil } from "@/utils/string.util";
export default defineComponent({
  components: { RunaiTooltip },
  inject: {
    entity: {
      default: EQuotaEntity.project,
    },
  },
  props: {
    tab: {
      type: String as PropType<EQuotaTab>,
      required: true,
    },
    isCpuEnabled: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    isOverQuotaPriorityEnabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isOnlyDefaultNodePool: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
  },
  computed: {
    headerConfig(): Record<EQuotaTab, HeaderConfig[]> {
      const priorityTooltip = this.isOnlyDefaultNodePool
        ? undefined
        : "The order of priority by which workloads will be scheduled. The order can be modified when submitting a specific workloads";
      return {
        [EQuotaTab.Scheduling]: [
          {
            name: EQuotaHeaderName.NodePoolPriority,
            label: EQuotaHeaderLabel.NodePoolPriority,
            tooltipText: priorityTooltip,
            aid: stringUtil.slug(EQuotaHeaderName.NodePoolPriority),
          },
          {
            name: EQuotaHeaderName.NodePool,
            label: EQuotaHeaderLabel.NodePool,
            aid: stringUtil.slug(EQuotaHeaderName.NodePool),
          },
          {
            name: EQuotaHeaderName.ProjectPriority,
            label: EQuotaHeaderLabel.ProjectPriority,
            tooltipText: `The ${this.entity}'s scheduling priority compared to other ${this.entity}s in the same node pool`,
            aid: stringUtil.slug(EQuotaHeaderName.ProjectPriority),
          },
          {
            name: EQuotaHeaderName.DepartmentPriority,
            label: EQuotaHeaderLabel.DepartmentPriority,
            tooltipText: `The ${this.entity}'s scheduling priority compared to other ${this.entity} in the same node pool`,
            aid: stringUtil.slug(EQuotaHeaderName.DepartmentPriority),
          },
          {
            name: EQuotaHeaderName.OverQuotaWeight,
            label: EQuotaHeaderLabel.OverQuotaWeight,
            aid: stringUtil.slug(EQuotaHeaderName.OverQuotaWeight),
          },
          {
            name: EQuotaHeaderName.OverQuota,
            label: EQuotaHeaderLabel.OverQuota,
            tooltipText: `The relative amount of additional unused resources that a ${this.entity} can get on top of its quota. This depends on the overall number of ${this.entity}s that requested quota overage and their weight settings.`,
            aid: stringUtil.slug(EQuotaHeaderName.OverQuota),
          },
          {
            name: EQuotaHeaderName.ProjectGpuLimit,
            label: EQuotaHeaderLabel.ProjectGpuLimit,
            tooltipText:
              "The maximum GPU device allocation the project can get from this node pool, representing the maximum sum of quota and over-quota GPUs",
            aid: stringUtil.slug(EQuotaHeaderName.ProjectGpuLimit),
          },
          {
            name: EQuotaHeaderName.DepartmentGpuLimit,
            label: EQuotaHeaderLabel.DepartmentGpuLimit,
            tooltipText:
              "The maximum GPU device allocation the department can get from this node pool, representing the maximum sum of quota and over-quota GPUs",
            aid: stringUtil.slug(EQuotaHeaderName.DepartmentGpuLimit),
          },
        ],
        [EQuotaTab.Quota]: [
          {
            name: EQuotaHeaderName.NodePoolPriority,
            label: EQuotaHeaderLabel.NodePoolPriority,
            tooltipText: priorityTooltip,
            aid: stringUtil.slug(EQuotaHeaderName.NodePoolPriority),
          },
          {
            name: EQuotaHeaderName.NodePool,
            label: EQuotaHeaderLabel.NodePool,
            aid: stringUtil.slug(EQuotaHeaderName.NodePool),
          },
          {
            name: EQuotaHeaderName.OverQuotaState,
            label: EQuotaHeaderLabel.OverQuotaState,
            tooltipText:
              "This indicates whether over-quota is enabled or disabled. You can change this in the Scheduling preferences tab.",
            aid: stringUtil.slug(EQuotaHeaderName.OverQuotaState),
          },
          {
            name: EQuotaHeaderName.GpuQuota,
            label: EQuotaHeaderLabel.GpuQuota,
            aid: stringUtil.slug(EQuotaHeaderName.GpuQuota),
          },
          {
            name: EQuotaHeaderName.CpuQuota,
            label: EQuotaHeaderLabel.CpuQuota,
            aid: stringUtil.slug(EQuotaHeaderName.CpuQuota),
          },
          {
            name: EQuotaHeaderName.CpuMemoryQuota,
            label: EQuotaHeaderLabel.CpuMemoryQuota,
            aid: stringUtil.slug(EQuotaHeaderName.CpuMemoryQuota),
          },
        ],
      };
    },
    labels(): HeaderConfig[] {
      const headerMap: Record<EQuotaHeaderName, boolean> = {
        [EQuotaHeaderName.OverQuotaState]: true,
        [EQuotaHeaderName.GpuQuota]: true,
        [EQuotaHeaderName.NodePoolPriority]: true,
        [EQuotaHeaderName.NodePool]: true,
        [EQuotaHeaderName.OverQuotaWeight]: this.isOverQuotaPriorityEnabled,
        [EQuotaHeaderName.OverQuota]: !this.isOverQuotaPriorityEnabled,
        [EQuotaHeaderName.ProjectGpuLimit]: !this.isDepartmentEntity,
        [EQuotaHeaderName.DepartmentGpuLimit]: this.isDepartmentEntity,
        [EQuotaHeaderName.CpuQuota]: this.isCpuEnabled,
        [EQuotaHeaderName.CpuMemoryQuota]: this.isCpuEnabled,
        [EQuotaHeaderName.ProjectPriority]: !this.isDepartmentEntity,
        [EQuotaHeaderName.DepartmentPriority]: this.isDepartmentEntity,
      };
      return this.headerConfig[this.tab].filter((header) => headerMap[header.name]);
    },
    isDepartmentEntity(): boolean {
      return this.entity === EQuotaEntity.department;
    },
    isSchedulingTab(): boolean {
      return this.tab === EQuotaTab.Scheduling;
    },
  },
  methods: {
    getHeaderLabel(header: HeaderConfig): string {
      if (header.name === EQuotaHeaderName.ProjectGpuLimit) {
        return "Project max. GPU <br> device allocation";
      }
      if (header.name === EQuotaHeaderName.DepartmentGpuLimit) {
        return "Department max. GPU <br> device allocation";
      }
      if (header.name === EQuotaHeaderName.NodePoolPriority && this.isOnlyDefaultNodePool) {
        return "";
      }
      if (header.name === EQuotaHeaderName.NodePool && this.isOnlyDefaultNodePool) {
        return "";
      }
      return header.label;
    },
  },
});
</script>
<style lang="scss" scoped>
.node-pools-table-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 12px;
  height: 49px;

  .node-pools-table-header__row {
    gap: 8px;
    display: flex;
    align-items: center;

    &.scheduling-tab {
      > *:nth-child(2) {
        flex-grow: 1;
      }
      > *:nth-child(3) {
        flex-shrink: 0;
        min-width: 135px;
      }
      > *:nth-child(n + 4) {
        flex-shrink: 0;
        min-width: 105px;
      }
    }
  }
  .node-pools-table-header__row > * {
    flex-shrink: 0;
    min-width: 105px;
  }
  .node-pools-table-header__row > *:first-child {
    width: 73px;
  }
  .node-pools-table-header__row > *:nth-child(2) {
    flex-grow: 1;
  }
  .node-pools-table-header__row > *:nth-child(3) {
    min-width: 110px;
  }
  .node-pools-table-header__cell {
    display: flex;
    align-items: center;
  }
}
</style>
